import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldPlus } from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import AuthLayout from '../../layouts/AuthLayout/AuthLayout';
import FormInput from '../../components/FormInput/FormInput';
import Checkbox from '../../components/Checkbox/Checkbox';
import Button from '../../components/Button/Button';
import api from '../../services/api';
import styles from './Login.module.css';

function Login() {
  usePageTitle('Log in');
  const navigate = useNavigate();
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors]             = useState({});
  const [generalError, setGeneralError] = useState('');
  const [loading, setLoading]           = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!email.trim())    errs.email    = 'Email is required';
    if (!password.trim()) errs.password = 'Password is required';
    if (Object.keys(errs).length) { setErrors(errs); setGeneralError(''); return; }

    setErrors({});
    setGeneralError('');
    setLoading(true);

    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.removeItem('isRecoverySession');
      localStorage.setItem('user', JSON.stringify({
        userId:               data.userId,
        firstName:            data.firstName,
        lastName:             data.lastName,
        email:                data.email,
        isAdmin:              data.isAdmin,
        userType:             data.userType,
        companyId:            data.companyId,
        accessLevel:          data.accessLevel,
        contractSigned:       data.contractSigned,
        recoveryContactEmail: data.recoveryContactEmail || '',
      }));
      navigate(data.isAdmin ? '/dashboard/overview' : '/dashboard/assets');
    } catch (err) {
      setGeneralError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearError = (field) => {
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
    if (generalError) setGeneralError('');
  };

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit}>
        <FormInput
          id="email"
          label="Email"
          type="email"
          autoComplete="username"
          placeholder="name@email.com"
          value={email}
          onChange={e => { setEmail(e.target.value); clearError('email'); }}
          error={errors.email}
        />

        <FormInput
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          placeholder="Insert password"
          value={password}
          onChange={e => { setPassword(e.target.value); clearError('password'); }}
          error={errors.password}
        />

        {generalError && (
          <p className={styles.generalError}>{generalError}</p>
        )}

        <div className="d-flex align-items-center justify-content-between mb-4">
          <Checkbox
            id="showPassword"
            label="Show password"
            checked={showPassword}
            onChange={() => setShowPassword(prev => !prev)}
          />
          <Link to="/forgot-password" className={styles.forgotPassword}>
            Forgot password?
          </Link>
        </div>

        <Button type="submit" disabled={loading}>
          {loading ? 'Logging in…' : 'Log in'}
        </Button>

        <Link to="/recovery-login" className={styles.recoveryLoginBtn}>
          <ShieldPlus size={18} />
          Log in with recovery email
        </Link>
      </form>
    </AuthLayout>
  );
}

export default Login;
